import { ColumnDef } from '@tanstack/react-table';
import { Archive, Ban, FileText, Send, Trash2 } from 'lucide-react';
import { SetStateAction } from 'react';
import { Pagination } from '../../../../lib/interfaces/utils';
import { formatDateTimeDE } from '../../../../lib/utils/date';
import DataTable from '../../../products/components/data-table/data-table';
import ListEmptyState from '../../../products/components/list-empty-state/list-empty-state';
import RowActionButton from '../../../products/components/row-action-button/row-action-button';
import { formatCents } from '../../../products/lib/money';
import { BillingTermMetaResponseDto } from '../../../products/types/response';
import { OfferListFilter } from '../../constants/offer-list-filter.enum';
import { formatOfferTerm } from '../../lib/billing-term';
import { formatCustomerPerson } from '../../lib/customer';
import { OfferConfirmAction } from '../../constants/offer-confirm-action.enum';
import { OfferDisplayStatus } from '../../constants/offer-display-status.enum';
import { canArchiveOffer, canSendOffer, getOfferDisplayStatus } from '../../lib/offer-status';
import { OfferListItemResponseDto } from '../../types/response';
import OfferStatusBadge from '../offer-status-badge/offer-status-badge';

const EMPTY_STATE: Record<OfferListFilter, { title: string; description: string }> = {
  [OfferListFilter.OPEN]: { title: 'Keine offenen Angebote', description: 'Erstelle ein neues Angebot.' },
  [OfferListFilter.CLOSED]: {
    title: 'Keine abgeschlossenen Angebote',
    description: 'Angenommene, zurückgezogene und abgelaufene Angebote erscheinen hier.'
  },
  [OfferListFilter.ARCHIVED]: {
    title: 'Keine archivierten Angebote',
    description: 'Abgeschlossene Angebote erscheinen hier, sobald du sie archivierst. Sie können dann nur noch angesehen werden.'
  }
};

export interface OfferListProps {
  offers: OfferListItemResponseDto[];
  billingTerms: BillingTermMetaResponseDto[];
  filter: OfferListFilter;
  pagination: Pagination;
  pageCount: number;
  onPaginationChange: (next: SetStateAction<Pagination>) => void;
  isLoading: boolean;
  /** Row click – a draft opens editable, every other offer read only. */
  onOpen: (offer: OfferListItemResponseDto) => void;
  onSend: (offer: OfferListItemResponseDto) => void;
  onViewOfferFile: (offer: OfferListItemResponseDto) => void;
  /** Delete a draft, revoke a sent offer or archive a closed one – after a confirm dialog. */
  onConfirm: (offer: OfferListItemResponseDto, action: OfferConfirmAction) => void;
}

const OfferList = ({
  offers,
  billingTerms,
  filter,
  pagination,
  pageCount,
  onPaginationChange,
  isLoading,
  onOpen,
  onSend,
  onViewOfferFile,
  onConfirm
}: OfferListProps) => {
  const columns: ColumnDef<OfferListItemResponseDto>[] = [
    {
      id: 'customer',
      header: 'Kunde',
      cell: ({ row }) => {
        const { customer } = row.original;
        const person = formatCustomerPerson(customer);
        return (
          <div className="flex flex-col min-w-0">
            <span className="truncate text-sm font-bold text-[#12282A]" title={customer.name ?? undefined}>
              {customer.name ?? person ?? '–'}
            </span>
            {customer.name && person && <span className="truncate text-[12px] text-[#888]">{person}</span>}
          </div>
        );
      }
    },
    {
      id: 'mail',
      header: 'E-Mail',
      size: 240,
      cell: ({ row }) => (
        <span className="block truncate text-sm text-gray-700" title={row.original.customer.mail}>
          {row.original.customer.mail}
        </span>
      )
    },
    {
      id: 'product',
      header: 'Produkt',
      size: 240,
      cell: ({ row }) => {
        const { product } = row.original;
        return (
          <div className="flex flex-col min-w-0">
            <span className="truncate text-sm text-[#12282A]" title={product.title}>
              {product.title}
            </span>
            <span className="truncate text-[12px] text-[#888]">
              {formatOfferTerm(billingTerms, product.term)} · {formatCents(product.monthlyPriceCents)} / Monat
            </span>
          </div>
        );
      }
    },
    {
      id: 'status',
      header: 'Status',
      size: 150,
      cell: ({ row }) => <OfferStatusBadge status={getOfferDisplayStatus(row.original)} />
    },
    {
      id: 'expiresAt',
      header: 'Gültig bis',
      size: 170,
      cell: ({ row }) => <span className="text-sm text-gray-700 whitespace-nowrap">{formatDateTimeDE(row.original.expiresAt)}</span>
    },
    {
      id: 'createdAt',
      header: 'Erstellt',
      size: 170,
      cell: ({ row }) => <span className="text-sm text-gray-700 whitespace-nowrap">{formatDateTimeDE(row.original.createdAt)}</span>
    },
    {
      id: 'actions',
      header: '',
      size: 130,
      cell: ({ row }) => {
        const status = getOfferDisplayStatus(row.original);
        const isDraft = status === OfferDisplayStatus.NOT_SENT;
        return (
          <div className="flex justify-end gap-1">
            {row.original.offerFile && (
              <RowActionButton label={`Angebots-PDF ansehen (${row.original.offerFile.fileName})`} onClick={() => onViewOfferFile(row.original)}>
                <FileText className="h-4 w-4" />
              </RowActionButton>
            )}
            {canArchiveOffer(row.original) && (
              <RowActionButton label="Archivieren" onClick={() => onConfirm(row.original, OfferConfirmAction.ARCHIVE)}>
                <Archive className="h-4 w-4" />
              </RowActionButton>
            )}
            {canSendOffer(row.original) && (
              <>
                <RowActionButton label={isDraft ? 'Versenden' : 'Erneut versenden'} onClick={() => onSend(row.original)}>
                  <Send className="h-4 w-4" />
                </RowActionButton>
                {isDraft ? (
                  <RowActionButton label="Entwurf löschen" onClick={() => onConfirm(row.original, OfferConfirmAction.DELETE)}>
                    <Trash2 className="h-4 w-4" />
                  </RowActionButton>
                ) : (
                  <RowActionButton label="Zurückziehen" onClick={() => onConfirm(row.original, OfferConfirmAction.REVOKE)}>
                    <Ban className="h-4 w-4" />
                  </RowActionButton>
                )}
              </>
            )}
          </div>
        );
      }
    }
  ];

  return (
    <DataTable
      columns={columns}
      data={offers}
      pagination={pagination}
      pageCount={pageCount}
      onPaginationChange={onPaginationChange}
      isLoading={isLoading}
      emptyState={<ListEmptyState {...EMPTY_STATE[filter]} />}
      onRowClick={onOpen}
    />
  );
};

export default OfferList;
